import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { Badge, Button } from '../../components/ui';
import { ROLES } from './teamRoles';

/**
 * Invite someone who does not have a Retail Circle account yet.
 *
 * The members list beside this can only add a colleague who already has one, by
 * user id — so a merchant could not bring in anyone new, and "Invite your team"
 * was a setup step with nowhere to go.
 *
 * Two ways to send, because dealers reach people two ways:
 *   - Email: we send it.
 *   - WhatsApp: the OWNER sends it, from their own number. A message from a
 *     name the recipient knows gets opened; one from us does not. We mint the
 *     link and hand it over.
 */


const waLink = (phone, text) =>
  `https://wa.me/${String(phone).replace(/\D+/g, '')}?text=${encodeURIComponent(text)}`;

export function TeamInvitePanel({ storeId, storeName, onChanged }) {
  const [method, setMethod] = useState('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('sales_admin');

  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);

  const reload = useCallback(async () => {
    if (!storeId) return;
    try {
      const { data } = await axios.get(`/api/stores/${storeId}/invites`);
      setInvites(Array.isArray(data) ? data : []);
    } catch (e) {
      setInvites([]);
    }
  }, [storeId]);

  useEffect(() => { reload(); }, [reload]);

  const send = async () => {
    setBusy(true);
    setError('');
    setSent(null);

    try {
      const body = { role };
      if (method === 'email') body.email = email.trim();
      else body.phone = phone.trim();

      const { data } = await axios.post(`/api/stores/${storeId}/invites`, body);

      setSent(data);
      setEmail('');
      setPhone('');
      await reload();
      onChanged?.();
    } catch (e) {
      const res = e?.response?.data;
      setError(res?.message || res?.errors?.email?.[0] || 'We couldn’t send that invitation.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (invite) => {
    try {
      await axios.delete(`/api/stores/${storeId}/invites/${invite.id}`);
      await reload();
      onChanged?.();
    } catch (e) {
      setError('We couldn’t cancel that invitation.');
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      // Clipboard can be blocked; the link is on screen either way.
    }
  };

  if (!storeId) return null;

  const canSend = !busy && (method === 'email' ? email.trim() : phone.trim());
  const shareText = `Join ${storeName || 'our team'} on Retail Circle: ${sent?.accept_url || ''}`;

  return (
    <div className="rc-invite">
      <h4 className="rc-invite__title">Invite your team</h4>
      <p className="rc-invite__intro">
        For someone who already has a Retail Circle account: they sign in and accept. New to
        Retail Circle? Add them by mobile above instead.
      </p>

      <div className="rc-invite__tabs" role="group" aria-label="How to invite">
        {['email', 'whatsapp'].map((m) => (
          <button
            key={m}
            type="button"
            className="rc-invite__tab"
            data-active={method === m ? 'true' : undefined}
            onClick={() => { setMethod(m); setError(''); setSent(null); }}
          >
            {m === 'email' ? 'By email' : 'By WhatsApp'}
          </button>
        ))}
      </div>

      <div className="rc-invite__form">
        {method === 'email' ? (
          <label className="rc-invite__field">
            <span className="rc-invite__label">Email address</span>
            <input
              type="email"
              className="rc-invite__input"
              value={email}
              placeholder="colleague@yourstore.com"
              onChange={(e) => setEmail(e.target.value)}
            />
            <span className="rc-invite__hint">We’ll send the invitation for you.</span>
          </label>
        ) : (
          <label className="rc-invite__field">
            <span className="rc-invite__label">WhatsApp number</span>
            <input
              type="tel"
              className="rc-invite__input"
              value={phone}
              placeholder="+44 7700 900200"
              onChange={(e) => setPhone(e.target.value)}
            />
            <span className="rc-invite__hint">
              You send this one yourself — we’ll open WhatsApp with the message ready.
            </span>
          </label>
        )}

        <label className="rc-invite__field">
          <span className="rc-invite__label">Role</span>
          <select className="rc-invite__input" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <span className="rc-invite__hint">{ROLES.find((r) => r.value === role)?.hint}</span>
        </label>
      </div>

      <Button variant="primary" size="lg" onClick={send} disabled={!canSend}>
        {busy ? 'Sending…' : 'Send invitation'}
      </Button>

      {error ? <p className="rc-invite__error">{error}</p> : null}

      {sent ? (
        <div className="rc-invite__sent">
          {sent.email && sent.delivered ? (
            <p className="rc-invite__sentline">Invitation emailed to {sent.email}.</p>
          ) : null}

          {sent.email && sent.delivered === false ? (
            <p className="rc-invite__sentline">
              We couldn’t send the email just now, but the invitation exists — copy the link below
              and send it yourself.
            </p>
          ) : null}

          {sent.phone ? (
            <p className="rc-invite__sentline">Ready to send to {sent.phone}.</p>
          ) : null}

          <div className="rc-invite__actions">
            {sent.phone ? (
              <Button
                variant="secondary"
                size="sm"
                href={waLink(sent.phone, shareText)}
                target="_blank"
                rel="noreferrer"
              >
                Open WhatsApp
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={() => copy(sent.accept_url)}>
              Copy invitation link
            </Button>
          </div>
        </div>
      ) : null}

      {invites.length ? (
        <div className="rc-invite__pending">
          <h5 className="rc-invite__pendingtitle">Waiting to be accepted</h5>
          <ul className="rc-invite__list">
            {invites.map((i) => (
              <li key={i.id} className="rc-invite__item">
                <span className="rc-invite__who">
                  <span className="rc-invite__whoname">{i.email || i.phone}</span>
                  <span className="rc-invite__whorole">
                    {ROLES.find((r) => r.value === i.role)?.label || i.role}
                  </span>
                </span>
                <Badge tone="muted" size="sm">Pending</Badge>
                <Button variant="outline" size="sm" onClick={() => copy(i.accept_url)}>Copy link</Button>
                <Button variant="ghost" size="sm" onClick={() => revoke(i)}>Cancel</Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default TeamInvitePanel;
